import { Icon } from "@iconify/react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  SwipeableDrawer,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import ColorThief from "color-thief-browser";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import NearbyStores from "./nearbyStores/products.nearbyStores.jsx";
import NearbyStoresMobile from "./nearbyStores/products.nearbyStoresMobile.jsx";
import ChatSkeleton from "./skeletons/Products.skeleton.Chat.jsx";

const CHAT_HISTORY_KEY = "chatHistory";

const Chat = () => {
  const { id } = useParams();
  const [width, setWidth] = useState("16px");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [botIsLoading, setBotIsLoading] = useState(false);
  const searchRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [bgColor, setBgColor] = useState("#f0f0f0");
  const [relatedProducts, setRelatedProducts] = useState([]); 
  const [nearbyStoresOpen, setNearbyStoresOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      // Redirect to /products/details/:id with ?openChat=true
      navigate(`/products/details/${id}?openChat=true`);
    }
  }, [isMobile, id, navigate]);

  

  const [product, setProduct] = useState({
    name: "",
    img: "",
    rating: "",
    description: "",
    ingredients: "",
    image: "",
    onlineStores: [],
    upc: "",
    Classifications: [],
    found: false,
  });
  

  const fetchData = useCallback(
    async (id) => {
      try {
        setLoading(true)
        console.log(id)
        const response = await axios.post("/dynamo_getByUPC", { upc: id });
        
        const newProduct = {
          name: response.data?.Name,
          rating: 4.5,
          company: response.data?.Company,
          data_from: response.data?.Data_from,
          weight: response.data?.Weight,
          description: response.data?.Description || '',
          ingredients: response.data?.Ingredients,
          nutrition: response.data?.Nutrition,
          image: response.data?.Image,
          onlineStores: response?.data?.Online_Stores,
          upc: response.data?.UPC,
          Classifications: response?.data?.Classifications
            ? [response?.data?.Classifications]
            : [],
          found: true,
        };
        setProduct(newProduct);
        const relatedProductsResponse = await axios.post("/get_additional_products", {
                product:
                  newProduct.name.split(" ").length > 4
                    ? newProduct.name.split(" ").slice(0, 3).join(" ")
                    : newProduct.name.split(" ")[0],
                UPC: newProduct.upc,
        })
        
          console.log("API Response (Related Products):", relatedProductsResponse.data.products);
          if (relatedProductsResponse.data.message === "No products found") {
            console.log("No similar products found.");
            setRelatedProducts([]); // Set to empty if no products found
          } else {
            let products = relatedProductsResponse.data.products.map((product) => ({
              title: product.Name,
              image: product.Image || `https://placehold.co/800x400?text=${product.Name}`,
              id: product.UPC,
            }));
            setRelatedProducts(products);
          }
          setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    },
    [id]);

  const extractColor = (img) => {
    if (!img || !img.complete) return;

    try {
      const colorThief = new ColorThief();
      const color = colorThief.getColor(img);
      console.log(color)
      setBgColor(`rgb(${color[0]}, ${color[1]}, ${color[2]})`);
    } catch (error) {
      console.warn("Error extracting color:", error);
    }
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const [messages, setMessages] = useState([
    {
      text: "Hi! Welcome to Flexday's customer service. How can I assist you today?",
      sender: "bot",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const hasProcessedPrompt = useRef(false);

  useEffect(() => {
    const savedChatData = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY));
    if (savedChatData && savedChatData.expiry > Date.now()) {
      if (savedChatData.upc === id) {
        setMessages(savedChatData.messages);
      }
      else {
        // Reset to default message for a different product
        setMessages([
          {
            text: "Hi! Welcome to Flexday's customer service. How can I assist you today?",
            sender: "bot",
          },
        ]);
      }
    }
  }, [id]);

  useEffect(() => {
    searchRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (messages.length > 1) {
      const chatData = {
        upc: id,
        messages: messages,
        expiry: Date.now() + 3600000, // 1 hour from now
      };

      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatData));
    }
  }, [messages, id]);

  useEffect(() => {
    if (!hasProcessedPrompt.current) {
      const prompt = searchParams.get("prompt");
      if (prompt) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: prompt, sender: "user" },
        ]);
        simulateBotResponse(prompt);
        hasProcessedPrompt.current = true;
      }
    }
  }, [searchParams]);

  const handleSendMessage = () => {
    if (inputValue.trim() !== "") {
      const userMessage = { text: inputValue.trim(), sender: "user" };
      setInputValue("");
      setBotIsLoading(true);
  
      const purchaseKeywords = [
        "where can i buy", "buy", "purchase", "order", "get this", 
        "find this", "available at", "shop", "store", "availability", "locate", "where"
      ];
  
      setMessages((prevMessages) => [...prevMessages, userMessage]); 
  
      if (purchaseKeywords.some(keyword => userMessage.text.toLowerCase().includes(keyword))) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { type: "nearby_stores", sender: "bot", text: "You can see nearby places where you can find this product." }
        ]);
        setBotIsLoading(false);
      } else {
        openaiResponse(userMessage.text, product.name, product.description, product.nutrition, product.ingredients)
          .then(() => setBotIsLoading(false))
          .catch((error) => {
            console.log("openai error: ", error);
            setBotIsLoading(false);
          });
      }
    }
  };
  
  
  const openaiResponse = async (question, product, description, nutrition, ingredients) => {
    try {
      const response = await axios.post("/chat/openai", {
        productName: product,
        question: question,
        productDescription: description,
        nutrition: nutrition,
        ingredients: ingredients,
      });
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: response.data.response, sender: "bot" },
      ]);
    } catch (error) {
      console.log(error);
    }
  };
  

  const simulateBotResponse = (userText) => {
    setBotIsLoading(true);
    setTimeout(() => {
      let botResponse;
      if (userText.toLowerCase().includes("feedback")) {
        botResponse =
          "Great! We appreciate your feedback. Could you please provide the name or model of the product?";
      } else if (
        userText.toLowerCase().includes("name") ||
        userText.toLowerCase().includes("model")
      ) {
        botResponse =
          "Thank you! What details would you like to include in your feedback?";
      } else {
        botResponse =
          "I’m here to help! Could you please clarify your request?";
      }

      setMessages((prevMessages) => [
        ...prevMessages,
        { text: botResponse, sender: "bot" },
      ]);
      setBotIsLoading(false);
    }, 1000);
  };

  return loading ? <ChatSkeleton /> : (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        backgroundColor: "#F8F9FA",
        padding: "20px 40px", // Added top, bottom, left, and right padding
        boxSizing: "border-box",
        paddingTop: "50px",
        paddingBottom: "30px",
        overflow: "hidden",
      }}
    >
      {/* Left Sidebar (Product Information) */}
      <Box
          sx={{
            width: "25%",
            backgroundColor: "white",
            borderRadius: "10px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Back Button & Title */}
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
            <IconButton onClick={() => navigate("/products/")} sx={{ marginRight: "10px" }}>
              <ArrowBackIosNewIcon fontSize="medium" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
              Talk to the product
            </Typography>
          </Box>

          {/* Product Image Holder - Fixed Size & Centered Image */}
          <Box
            sx={{
              width: "100%",
              height: "180px", // Fixed height to maintain consistency
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white", // Light gray background
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <img
              src={product.image ? product.image : `https://placehold.co/400x400?text=${product.name}`}
              alt="Product"
              style={{
                maxWidth: "80%", // Ensures image is not too big
                maxHeight: "100%", // Fits within the box
                objectFit: "contain", // Ensures it stays within the boundaries
              }}
            />
          </Box>

          {/* Product Name */}
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 2, textAlign: "center" }}>
            {product.name}
          </Typography>

          {/* Product Description */}
          <Box 
              sx={{ 
                width: "100%", 
                textAlign: "center", 
                mt: 1, 
                position: "relative"
              }}
            >
              <Box
                sx={{
                  maxHeight: showFullDescription ? "150px" : "42px",
                  overflowY: showFullDescription ? "auto" : "hidden", 
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: showFullDescription ? "unset" : 2, // Show only 2 lines initially
                  WebkitBoxOrient: "vertical",
                  backgroundColor: "#f9f9f9",
                  padding: "1px",
                  borderRadius: "4px",
                  scrollbarWidth: "thin", // For Firefox
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#aaa",
                    borderRadius: "4px",
                  }
                }}
              >
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  {product.description}
                </Typography>
              </Box>

              {/* Show "See more" button only if text is truncated */}
              {product.description.length > 100 && (
                <Button 
                  size="small" 
                  onClick={() => setShowFullDescription(!showFullDescription)} 
                  sx={{ mt: 1, color: "#007BFF", textTransform: "none" }}
                >
                  {showFullDescription ? "See less" : "See more"}
                </Button>
              )}
            </Box>

          {/* Related Searches - Now scrollable & fetching from API */}
          {relatedProducts.length != 0 ? (
            <Typography variant="h6" sx={{ mt: 3, fontWeight: "bold" }}>
              Related <span style={{ color: "#EF4F26" }}>Searches</span>
            </Typography>
          ) : (
            <Box />
          )}


          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              mt: 2,
              maxHeight: "200px", // Limiting height for scrolling
              overflowY: "auto", // Enables scrolling
              paddingRight: "5px", // Prevents content from touching scrollbar
            }}
          >
            {relatedProducts.length === 0 ? (
              <Box />
            ) : (
              relatedProducts.map((item) => (
                <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between", // Ensures space for chat icon
                  backgroundColor: "#F5F5F5",
                  padding: "10px",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                {/* Product Image & Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/products/details/${item.id}`)} // Click to navigate to details
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{ borderRadius: "8px", marginRight: "10px", width: "50px", height: "50px" }}
                  />
                  <Typography variant="body2">{item.title}</Typography>
                </Box>
        
                {/* Chat Icon - Click to Navigate to Chat */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/products/chat/${item.id}`);
                  }}
                >
                  <Icon
                    icon="heroicons:chat-bubble-left-ellipsis"
                    fontSize="large"
                    style={{ color: "#99B2C3" }}
                  />
                </IconButton>
              </Box>
              ))
            )}
          </Box>
        </Box>


      {/* Chat Section */}
          <Box
            sx={{
              flex: 2,
              backgroundColor: "white",
              borderRadius: "10px",
              backgroundColor: "#E9F4FB",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflow: "hidden",
              marginLeft: "25px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto", paddingRight: "10px" , paddingBottom: "15px"}}>
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.sender === "user" ? "flex-end" : "flex-start",
                    gap: "8px",
                    marginBottom: "16px",
                  }}
                >
                  {message.sender === "bot" && (
                    <Box
                      sx={{
                        width: "35px",
                        height: "35px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#FF5F1F", 
                      }}
                    >
                      <img 
                        src="/botLogo.svg" 
                        alt="Bot" 
                        style={{ width: "80%", height: "80%", objectFit: "contain" }} 
                      />
                    </Box>
                  )}
                  
                  {message.type === "nearby_stores" ? (
                    <Box
                      sx={{
                        backgroundColor: "#e0e0e0",
                        color: "#000",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        maxWidth: "55%",
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2">{message.text}</Typography>
                      {isMobile ? (
                        <><IconButton
                        onClick={() => setNearbyStoresOpen(true)}
                          sx={{
                            width: "194px",
                            height: "53px",
                            borderRadius: "30px",
                            background: "#E8E8E8",
                            gap: 1,
                            "&:hover": {
                              background: "#E8E8E8",
                              opacity: 1,
                            },
                          }}
                        >
                          <Icon icon={"bx:store"} style={{ fontWeight: 800, color: "#205375" }} />
                          <Typography variant="h6" sx={{ color: "#205375" }}>
                            Store Nearby
                          </Typography>
                        </IconButton><SwipeableDrawer
                          anchor={"bottom"}
                          open={nearbyStoresOpen}
                          onClose={() => setNearbyStoresOpen(false)}
                          onOpen={() => setNearbyStoresOpen(true)}
                        >
                            <Box
                              sx={{
                                mt: 2,
                                maxHeight: "60vh",
                                minHeight: "65vh",
                                py: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 3,
                                  position: "fixed",
                                  width: "100%",
                                  transform: "translateY(-2vh)",
                                  // backgroundColor: "white",
                                }}
                              >
                                <Box sx={{ px: 2 }}>
                                  <Typography
                                    variant="h5"
                                    color="#205375"
                                    component="div"
                                    sx={{ fontWeight: 600, textAlign: "left", mb: 2 }}
                                  >
                                    Nearby{" "}
                                    <span style={{ color: "#EF4F26" }}>Stores</span>
                                  </Typography>
                                </Box>
                                <Box sx={{ zIndex: 1, px: 2 }}>
                                  <span
                                    style={{
                                      border: "1px solid #205375",
                                      padding: "5px 10px",
                                      borderRadius: 15,
                                    }}
                                  >
                                    <Button onClick={() => setNearbyStoresOpen(false)}>
                                      Close
                                    </Button>
                                  </span>
                                </Box>
                              </Box>

                              <NearbyStoresMobile
                                product={product}
                                open={nearbyStoresOpen} />
                            </Box>
                          </SwipeableDrawer></>
                      ) : (
                        <NearbyStores product={product} />
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        backgroundColor:
                          message.sender === "user" ? "#0073b1" : "#E0E0E0",
                        color: message.sender === "user" ? "white" : "black",
                        padding: "8px 12px",
                        borderRadius: "12px",
                        maxWidth: "60%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          paddingLeft: "6px", 
                          paddingRight: "8px", 
                          "& ul": {
                            paddingLeft: "20px",
                          },
                          "& li": {
                            marginBottom: "4px", 
                          },
                        }}
                      >
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                      </Typography>

                    </Box>
                  )}
                </Box>
              ))}
              {botIsLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#e0e0e0",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      maxWidth: "75%",
                    }}
                  >
                    <Typography variant="body2">...</Typography>
                  </Box>
                </Box>
              )}
              <div ref={searchRef} />
            </Box>
  
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                sx={{
                  backgroundColor: "white", 
                  borderRadius: "25px", 
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", 
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#ccc", 
                    },
                    "&:hover fieldset": {
                      borderColor: "#aaa", 
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#007BFF", 
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    backgroundColor: "white", 
                    borderRadius: "25px", 
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSendMessage} sx={{ color: "#FF5F1F" }}> 
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

          </Box>
    </Box>
  );
}

export default Chat;
